import { useState, useEffect } from 'react';
import { useData } from '../../utils/hooks/useData';

import AnalyticsError from '../AnalyticsError';

import { useAnalyticsStoreContext } from '../contexts';
import { calculatePercentageDifference, calculateTimeDifference, formatFromAndToQuery } from '../lib';

export default function NewVisitorsAndAverageTimeOnSite({ dataEndpoint }) {
  const dateRange = useAnalyticsStoreContext((state) => state.data.first.dateRange);
  const dateRangeTwo = useAnalyticsStoreContext((state) => state.data.second.dateRange);
  const isComparing = useAnalyticsStoreContext((state) => state.isComparing);

  const { data: dataOne, isLoading: isLoadingOne, error: errorOne } = useData(`${dataEndpoint}.json?${formatFromAndToQuery(dateRange)}`);

  const {
    data: dataTwo,
    isLoading: isLoadingTwo,
    error: errorTwo,
  } = useData(`${dataEndpoint}.json?${formatFromAndToQuery(dateRangeTwo)}`, isComparing && dateRangeTwo.from && dateRangeTwo.to, {
    average_time_on_site: null,
    new_visitors: null,
  });

  const [data, setData] = useState({ first: { average_time_on_site: null, new_visitors: null }, second: { average_time_on_site: null, new_visitors: null } });

  useEffect(() => {
    if (dataOne && dataOne.new_visitors && dataOne.average_time_on_site) {
      setData((prevData) => ({
        ...prevData,
        first: { average_time_on_site: dataOne.average_time_on_site, new_visitors: dataOne.new_visitors },
      }));
    }

    if (!isComparing) {
      setData((prevData) => ({
        ...prevData,
        second: { average_time_on_site: null, new_visitors: null },
      }));

      return;
    }

    if (dataTwo && dataTwo.new_visitors && dataTwo.average_time_on_site) {
      setData((prevData) => ({
        ...prevData,
        second: { average_time_on_site: dataTwo.average_time_on_site, new_visitors: dataTwo.new_visitors },
      }));
    }
  }, [dataOne, dataTwo, isComparing]);

  return (
    <>
      <div className="col-12 col-lg-6 col-xl">
        <div className="card">
          <div className="card-body" data-bs-toggle="tooltip" title="New visitors in the current time range">
            <div className="row align-items-center gx-0">
              <div className="col">
                <h6 className="text-uppercase text-muted mb-2">New Visitors</h6>
                {isLoadingOne || isLoadingTwo ? (
                  <div className="placeholder-glow">
                    <span className="placeholder rounded bg-secondary col-6" />
                  </div>
                ) : errorOne || errorTwo ? (
                  <div className="d-flex flex-column">
                    {errorOne && (
                      <div className="d-flex gap-2">
                        <span>First Dataset:</span>
                        <AnalyticsError errorMsg={errorOne} textStyle={{ maxWidth: 150 }} />
                      </div>
                    )}
                    {errorTwo && (
                      <div className="d-flex gap-2">
                        <span>Second Dataset:</span>
                        <AnalyticsError errorMsg={errorTwo} textStyle={{ maxWidth: 150 }} />
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="d-flex gap-2">
                    <span className="h2 mb-0">{data.first.new_visitors}</span>
                    {data.second.new_visitors && (
                      <span className="h3 mb-0 text-muted">
                        {data.second.new_visitors} ({calculatePercentageDifference(data.first.new_visitors, data.second.new_visitors)})
                      </span>
                    )}
                  </div>
                )}
              </div>
              <div className="col-auto">
                <span className="h2 fe fe-log-out text-muted mb-0" />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="col-12 col-lg-6 col-xl">
        <div className="card">
          <div className="card-body" data-bs-toggle="tooltip" title="Average time on site in the current time range">
            <div className="row align-items-center gx-0">
              <div className="col">
                <h6 className="text-uppercase text-muted mb-2">Avg. Time on site</h6>
                {isLoadingOne || isLoadingTwo ? (
                  <div className="placeholder-glow">
                    <span className="placeholder rounded bg-secondary col-6" />
                  </div>
                ) : errorOne || errorTwo ? (
                  <div className="d-flex flex-column">
                    {errorOne && (
                      <div className="d-flex gap-2">
                        <span>First Dataset:</span>
                        <AnalyticsError errorMsg={errorOne} textStyle={{ maxWidth: 150 }} />
                      </div>
                    )}
                    {errorTwo && (
                      <div className="d-flex gap-2">
                        <span>Second Dataset:</span>
                        <AnalyticsError errorMsg={errorTwo} textStyle={{ maxWidth: 150 }} />
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="d-flex gap-2">
                    <span className="h2 mb-0">{data.first.average_time_on_site}</span>
                    {data.second.average_time_on_site && (
                      <span className="h3 mb-0 text-muted">
                        {data.second.average_time_on_site} ({calculateTimeDifference(data.first.average_time_on_site, data.second.average_time_on_site)})
                      </span>
                    )}
                  </div>
                )}
              </div>
              <div className="col-auto">
                <span className="h2 fe fe-clock text-muted mb-0" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
